import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useState } from 'react';

import {
  type NotificationPageParamsType,
  type PageMetaNotificationNameType,
  useNotificationPageMetaByKey,
} from '@/api/settings';
import EnvelopeIcon from '@/assets/svg/bx-envelope.svg?react';
import ShareIcon from '@/assets/svg/bx-share.svg?react';
import TargetLockIcon from '@/assets/svg/bx-target-lock.svg?react';
import MagicEmailIcon from '@/assets/svg/magic-email.svg?react';
import { Button } from '@/components/buttons';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/DropdownMenu';
import { usePageFilterContext } from '@/components/page';
import {
  GoalNotificationSubscriptionDialog,
  RecurringNotificationSubscriptionDialog,
} from '@/features/NotificationSubscriptionModal';
import { useNotification } from '@/providers/Notification';

type Props = {
  notificationPageKey: PageMetaNotificationNameType;
  notificationParams?: NotificationPageParamsType;
};

type ItemKey = 'recurring' | 'goal' | 'ai_digest';

const ShareMenu = ({ notificationPageKey, notificationParams }: Props) => {
  const notificationPageMeta = useNotificationPageMetaByKey(notificationPageKey);
  const hasGoalNotifications = notificationPageMeta.kpiMetrics != null;
  const {
    pageFilters: { audience },
  } = usePageFilterContext();
  const [selectedMenuItem, setSelectedMenuItem] = useState<ItemKey | null>(null);
  const { pushNotification } = useNotification();

  const handleSaveComplete = (success: boolean) => {
    setSelectedMenuItem(null);
    if (success) {
      pushNotification({ type: 'success', message: t`Notification scheduled successfully` });
    } else {
      pushNotification({
        type: 'error',
        message: t`Failed to schedule notification. Please try again or contact support if you encounter the issue again.`,
      });
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button aria-label={t`Share`} shape="square" size="icon-large">
              <ShareIcon />
            </Button>
          }
          openOnHover
        />
        <DropdownMenuContent className="min-w-45" align="end">
          {hasGoalNotifications ? (
            <DropdownMenuItem onClick={() => setSelectedMenuItem('goal')}>
              <TargetLockIcon />
              <Trans>Goal Notification</Trans>
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem onClick={() => setSelectedMenuItem('recurring')}>
            <EnvelopeIcon />
            <Trans>Schedule Email</Trans>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setSelectedMenuItem('ai_digest')}>
            <MagicEmailIcon />
            <Trans>Schedule AI Digest</Trans>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {selectedMenuItem &&
        (selectedMenuItem === 'goal' ? (
          <GoalNotificationSubscriptionDialog
            audience={audience}
            notificationPageMeta={notificationPageMeta}
            notificationParams={notificationParams}
            onCancel={() => setSelectedMenuItem(null)}
            onSaveComplete={handleSaveComplete}
          />
        ) : (
          <RecurringNotificationSubscriptionDialog
            audience={audience}
            notificationPageMeta={notificationPageMeta}
            notificationType={selectedMenuItem === 'ai_digest' ? 'ai_digest' : 'recurring'}
            onCancel={() => setSelectedMenuItem(null)}
            onSaveComplete={handleSaveComplete}
          />
        ))}
    </>
  );
};

export default ShareMenu;
